import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAdminAuth } from '../auth/useAdminAuth';
import useAdminData from '../components/shared/useAdminData';
import SkeletonLoaderComponents from '../components/shared/SkeletonLoader';
import ErrorBoundary from '../components/shared/ErrorBoundary';
import Modal from '../components/common/Modal';
import StatusBadge from '../components/common/StatusBadge';
import { API_ENDPOINTS } from '../config/api-endpoints';
import './Notifications.css';

const { Skeleton } = SkeletonLoaderComponents;

const Notifications = () => {
  const { user } = useAdminAuth();
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    targetType: 'all',
    targetValue: ''
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch notification history
  const { data: notificationsData, loading, error: fetchError, refetch } = useAdminData(
    'notifications',
    API_ENDPOINTS.ADMIN.NOTIFICATIONS,
    {}
  );
  const notifications = Array.isArray(notificationsData) ? notificationsData : (notificationsData?.notifications || []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTargetTypeChange = (e) => {
    setFormData(prev => ({
      ...prev,
      targetType: e.target.value,
      targetValue: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validation
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!formData.content.trim()) {
      setError('Content is required');
      return;
    }
    if (formData.targetType !== 'all' && !formData.targetValue.trim()) {
      setError('Target value is required for specific targeting');
      return;
    }

    setSending(true);

    try {
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();
      const response = await fetch(API_ENDPOINTS.ADMIN.NOTIFICATIONS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: formData.title,
          content: formData.content,
          targetType: formData.targetType,
          targetValue: formData.targetValue,
          sentBy: user.userId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send notification');
      }

      const result = await response.json();
      setSuccess(`Notification sent successfully to ${result.deliveryCount || 0} users`);
      setFormData({
        title: '',
        content: '',
        targetType: 'all',
        targetValue: ''
      });
      setShowComposeModal(false);
      refetch();
    } catch (err) {
      setError(err.message || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const getTargetDescription = (notification) => {
    switch (notification.targetType) {
      case 'all':
        return 'All Users';
      case 'group':
        return `Group: ${notification.targetValue}`;
      case 'user':
        return `User: ${notification.targetValue}`;
      default:
        return notification.targetType;
    }
  };

  if (loading) {
    return (
      <div className="notifications">
        <div className="notifications__header">
          <h1 className="notifications__title">Notification Management</h1>
        </div>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} width="100%" height="80px" className="admin-skeleton-gap" />
        ))}
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="notifications">
        <div className="notifications__header">
          <h1 className="notifications__title">Notification Management</h1>
          <button
            className="notifications__compose-btn"
            onClick={() => setShowComposeModal(true)}
          >
            Compose Notification
          </button>
        </div>

        {error && (
          <div className="notifications__alert notifications__alert--error">
            {error}
          </div>
        )}

        {success && (
          <div className="notifications__alert notifications__alert--success">
            {success}
          </div>
        )}

        {fetchError && (
          <div className="notifications__alert notifications__alert--error">
            Failed to load notifications: {fetchError}
          </div>
        )}

        <div className="notifications__history">
          <h2 className="notifications__section-title">Notification History</h2>
          
          {notifications.length === 0 ? (
            <div className="notifications__empty">
              No notifications sent yet. Click "Compose Notification" to send your first one.
            </div>
          ) : (
            <div className="notifications__list">
              {notifications.map((notification) => (
                <div key={notification.notificationId} className="notifications__item">
                  <div className="notifications__item-header">
                    <h3 className="notifications__item-title">{notification.title}</h3>
                    <span className="notifications__item-date">
                      {new Date(notification.sentAt).toLocaleString()}
                    </span>
                  </div>
                  
                  <p className="notifications__item-content">{notification.content}</p>
                  
                  <div className="notifications__item-meta">
                    <div className="notifications__item-target">
                      <strong>Target:</strong> {getTargetDescription(notification)}
                    </div>
                    <div className="notifications__item-stats">
                      <span className="notifications__stat">
                        <strong>Delivered:</strong> {notification.deliveryCount || 0}
                      </span>
                      <span className="notifications__stat">
                        <strong>Read:</strong> {notification.readCount || 0}
                      </span>
                      <span className="notifications__stat">
                        <strong>Read Rate:</strong>{' '}
                        {notification.deliveryCount > 0
                          ? `${Math.round((notification.readCount / notification.deliveryCount) * 100)}%`
                          : '0%'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="notifications__item-footer">
                    <span className="notifications__item-sender">
                      Sent by: {notification.sentBy}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showComposeModal && (
          <Modal
            title="Compose Notification"
            onClose={() => setShowComposeModal(false)}
          >
            <form onSubmit={handleSubmit} className="notifications__form">
              <div className="notifications__form-group">
                <label htmlFor="title" className="notifications__label">
                  Title *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="notifications__input"
                  placeholder="Enter notification title"
                  required
                />
              </div>

              <div className="notifications__form-group">
                <label htmlFor="content" className="notifications__label">
                  Content *
                </label>
                <textarea
                  id="content"
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  className="notifications__textarea"
                  placeholder="Enter notification content"
                  rows="5"
                  required
                />
              </div>

              <div className="notifications__form-group">
                <label htmlFor="targetType" className="notifications__label">
                  Target Audience *
                </label>
                <select
                  id="targetType"
                  name="targetType"
                  value={formData.targetType}
                  onChange={handleTargetTypeChange}
                  className="notifications__select"
                  required
                >
                  <option value="all">All Users</option>
                  <option value="group">Specific Group</option>
                  <option value="user">Individual User</option>
                </select>
              </div>

              {formData.targetType === 'group' && (
                <div className="notifications__form-group">
                  <label htmlFor="targetValue" className="notifications__label">
                    Group Name *
                  </label>
                  <input
                    type="text"
                    id="targetValue"
                    name="targetValue"
                    value={formData.targetValue}
                    onChange={handleInputChange}
                    className="notifications__input"
                    placeholder="e.g., Premium, Free, Admins"
                    required
                  />
                </div>
              )}

              {formData.targetType === 'user' && (
                <div className="notifications__form-group">
                  <label htmlFor="targetValue" className="notifications__label">
                    User ID *
                  </label>
                  <input
                    type="text"
                    id="targetValue"
                    name="targetValue"
                    value={formData.targetValue}
                    onChange={handleInputChange}
                    className="notifications__input"
                    placeholder="Enter user ID"
                    required
                  />
                </div>
              )}

              <div className="notifications__form-actions">
                <button
                  type="button"
                  onClick={() => setShowComposeModal(false)}
                  className="notifications__btn notifications__btn--secondary"
                  disabled={sending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="notifications__btn notifications__btn--primary"
                  disabled={sending}
                >
                  {sending ? 'Sending...' : 'Send Notification'}
                </button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default Notifications;
